import { useEffect } from 'react';
import { Button, Form, Input } from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { normalizePhone } from '@/pages/POS/components/CustomerSidebar/addressBook';

// TCR-2384 — danh sách "Người nhận khác" của một điểm giao (dùng chung tab khách + sổ địa chỉ POS).
const MAX_RECEIVERS = 10;
const PHONE_PATTERN = /^(0|\+84)[3-9]\d{8}$/;
const PHONE_MESSAGE = 'Số điện thoại không hợp lệ (VD: 0912345678)';

interface Props {
  name?: string;
  /** Tên field SĐT chính trong form chứa component này (để chặn trùng). */
  mainPhoneField?: string;
}

// Hàng trống hoàn toàn được bỏ qua lúc gửi; hàng đã nhập dở thì tên + SĐT là bắt buộc.
type ReceiverRow = { name?: string; phone?: string; note?: string } | undefined;
const isBlank = (v?: string) => !v || !v.trim();

export function AddressReceiversField({ name = 'receivers', mainPhoneField = 'phone' }: Props) {
  const form = Form.useFormInstance();
  const mainPhone = Form.useWatch(mainPhoneField, form);
  // Sửa SĐT chính sau khi đã nhập người nhận: kiểm lại luật cấp danh sách (trùng SĐT) cho đúng ngay.
  useEffect(() => {
    const list = form.getFieldValue(name) as unknown[] | undefined;
    if (list && list.length > 0) form.validateFields([name], { recursive: false }).catch(() => undefined);
  }, [mainPhone, form, name]);

  const requiredWhenRowFilled = (index: number, key: 'name' | 'phone', message: string) =>
    ({ getFieldValue }: { getFieldValue: (n: (string | number)[]) => unknown }) => ({
      validator: async () => {
        const row = getFieldValue([name, index]) as ReceiverRow;
        const rowFilled = !isBlank(row?.name) || !isBlank(row?.phone) || !isBlank(row?.note);
        if (rowFilled && isBlank(row?.[key])) throw new Error(message);
      },
    });

  return (
    <Form.Item label="Người nhận khác" style={{ marginBottom: 16 }}>
      <Form.List
        name={name}
        rules={[
          {
            validator: async (_, list: Array<{ phone?: string } | undefined> | undefined) => {
              const phones = (list ?? []).map((r) => normalizePhone(r?.phone)).filter(Boolean);
              if (new Set(phones).size !== phones.length) throw new Error('Trùng số điện thoại');
            },
          },
          {
            validator: async (_, list: Array<{ phone?: string } | undefined> | undefined) => {
              const main = normalizePhone(String(form.getFieldValue(mainPhoneField) ?? ''));
              if (!main) return;
              if ((list ?? []).some((r) => normalizePhone(r?.phone) === main)) throw new Error('Trùng số điện thoại');
            },
          },
        ]}
      >
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map((field) => (
              <div key={field.key} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                <Form.Item name={[field.name, 'name']} dependencies={[[name, field.name, 'phone'], [name, field.name, 'note']]} style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
                  rules={[
                    requiredWhenRowFilled(field.name, 'name', 'Nhập tên'),
                    { max: 50, message: 'Tối đa 50 ký tự' },
                  ]}>
                  <Input placeholder="Tên" maxLength={50} />
                </Form.Item>
                <Form.Item name={[field.name, 'phone']} dependencies={[[name, field.name, 'name'], [name, field.name, 'note']]} style={{ flex: '0 0 116px', marginBottom: 0 }}
                  rules={[
                    requiredWhenRowFilled(field.name, 'phone', 'Nhập SĐT'),
                    { pattern: PHONE_PATTERN, message: PHONE_MESSAGE },
                  ]}>
                  <Input placeholder="Số điện thoại" maxLength={15} />
                </Form.Item>
                <Form.Item name={[field.name, 'note']} style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
                  rules={[{ max: 100, message: 'Tối đa 100 ký tự' }]}>
                  <Input placeholder="Ghi chú" maxLength={100} />
                </Form.Item>
                <Button type="text" aria-label="Xoá người nhận" icon={<CloseOutlined />} onClick={() => remove(field.name)} />
              </div>
            ))}
            {fields.length < MAX_RECEIVERS ? (
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ name: '', phone: '', note: '' })} block>
                Người nhận khác
              </Button>
            ) : null}
            <Form.ErrorList errors={errors} />
          </>
        )}
      </Form.List>
    </Form.Item>
  );
}
